import * as React from 'react';
import { observer } from 'mobx-react';
import i18next from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Organization, ProfileType, Site } from '@ixicare/resources';
import { Alert, IconButton, Panel, Select, Spinner } from '@ixicare/ui';
import dashboardWrapper from '~/wrappers/dashboard';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { downloadAsBlob } from '~utils/export';
import ErrorPage from '~/pages/ErrorPage';
import { buildOrgOptions, buildUnassignedSiteRows, findOrgNode, flattenBillingRows, NO_ORG_VALUE } from './orgTree';
import { useBillingBreakdown } from './useBillingBreakdown';
import { BillingTable } from './BillingTable';
import { buildBillingCsv } from './billingCsv';
import { deviceTypeLabel, isHiddenDeviceType, profileGroup, profileTypeLabel } from './billingLabels';

const unique = (values: string[]): string[] => Array.from(new Set(values));

// Billing usage is only tracked from June 2026 onward; earlier periods aren't selectable.
const BILLING_START_YEAR = 2026;
const BILLING_START_MONTH = 6;

const BillingPage: React.FC = observer(() => {
  const { permissions, auth } = useStores();

  const now = new Date();
  const [year, setYear] = React.useState<number>(now.getFullYear());
  const [month, setMonth] = React.useState<number>(now.getMonth() + 1);
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);

  // Only Watcherr employees manage sites across orgs, so only they get the
  // "no organization" bucket of orphan sites. Org admins never see it.
  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;

  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: Organization.getOrganizations,
  });
  const roots = organizationsQuery.data ?? [];

  const sitesQuery = useQuery({
    queryKey: ['sites'],
    queryFn: () => Site.getSites(),
    enabled: isEmployee,
  });
  const unassignedSites = React.useMemo(
    () => (sitesQuery.data ?? []).filter((site) => site.organizationId === null),
    [sitesQuery.data]
  );

  const orgOptions = React.useMemo(() => {
    const options = buildOrgOptions(roots);
    if (isEmployee) options.unshift({ value: NO_ORG_VALUE, label: t('billing.noOrganization') });
    return options;
  }, [roots, isEmployee]);

  // Org admins have a single scoped root → preselect it. Watcherr employees see
  // multiple top-level orgs and must pick one before any billing is shown.
  React.useEffect(() => {
    if (selectedOrgId) return;
    if (roots.length === 1) setSelectedOrgId(roots[0].id);
  }, [roots, selectedOrgId]);

  const isNoOrg = selectedOrgId === NO_ORG_VALUE;
  const selectedNode = React.useMemo(
    () => (selectedOrgId && !isNoOrg ? findOrgNode(roots, selectedOrgId) : undefined),
    [roots, selectedOrgId, isNoOrg]
  );
  const hasSelection = isNoOrg || !!selectedNode;

  const billingRows = React.useMemo(() => {
    if (isNoOrg) return buildUnassignedSiteRows(unassignedSites);
    return selectedNode ? flattenBillingRows(selectedNode) : undefined;
  }, [isNoOrg, unassignedSites, selectedNode]);

  const breakdown = useBillingBreakdown(billingRows, selectedOrgId, year, month);
  const rows = breakdown.data ?? [];

  const deviceTypes = React.useMemo(
    () =>
      unique(rows.flatMap((row) => row.usage.devices.items.map((item) => item.deviceType)))
        .filter((type) => !isHiddenDeviceType(type))
        .sort((a, b) => deviceTypeLabel(a).localeCompare(deviceTypeLabel(b))),
    [rows]
  );
  const profileTypes = React.useMemo(
    () =>
      unique(rows.flatMap((row) => row.usage.profiles.items.map((item) => profileGroup(item.profileType)))).sort(
        (a, b) => profileTypeLabel(a).localeCompare(profileTypeLabel(b))
      ),
    [rows]
  );

  // In the start year the months below June are hidden; later years show all 12.
  const monthOptions = React.useMemo(() => {
    const firstMonth = year === BILLING_START_YEAR ? BILLING_START_MONTH : 1;
    return Array.from({ length: 12 - firstMonth + 1 }, (_, index) => {
      const monthNumber = firstMonth + index;
      return {
        value: monthNumber,
        label: new Date(2000, monthNumber - 1, 1).toLocaleString(i18next.language, { month: 'long' }),
      };
    });
  }, [year]);
  const yearOptions = React.useMemo(() => {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= BILLING_START_YEAR; y--) years.push(y);
    return years.map((y) => ({ value: y, label: String(y) }));
  }, []);

  // Switching to the start year can leave the month before the floor — clamp it up.
  React.useEffect(() => {
    if (year === BILLING_START_YEAR && month < BILLING_START_MONTH) setMonth(BILLING_START_MONTH);
  }, [year, month]);

  if (!permissions.has('can_view_billing')) {
    return <ErrorPage statusCode={404} />;
  }

  const handleExport = () => {
    const csv = buildBillingCsv(rows, deviceTypes, profileTypes);
    downloadAsBlob(`billing-${year}-${String(month).padStart(2, '0')}.csv`, csv);
  };

  const renderBody = () => {
    if (!hasSelection) {
      return <p>{t('billing.selectOrganizationPrompt')}</p>;
    }
    if (breakdown.isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner />
        </div>
      );
    }
    if (breakdown.isError) {
      return <p>{t('billing.loadError')}</p>;
    }
    if (rows.length === 0) {
      return <p>{t('billing.empty')}</p>;
    }
    return <BillingTable rows={rows} deviceTypes={deviceTypes} profileTypes={profileTypes} />;
  };

  return (
    <Panel>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 280 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.organization') + ':'}</label>
          <div style={{ flex: 1 }}>
            <Select
              value={selectedOrgId}
              onChange={(value: string) => setSelectedOrgId(value)}
              options={orgOptions}
              placeholder={t('site.form.organizationPlaceholder')}
              as="filter"
            />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.month') + ':'}</label>
          <Select value={month} onChange={(value: number) => setMonth(value)} options={monthOptions} as="filter" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.year') + ':'}</label>
          <Select value={year} onChange={(value: number) => setYear(value)} options={yearOptions} as="filter" />
        </div>
        {rows.length > 0 && (
          <div style={{ marginLeft: 'auto' }}>
            <IconButton lucide={Download} onClick={handleExport} />
          </div>
        )}
      </div>
      {renderBody()}
      <div style={{ marginTop: 24 }}>
        <Alert type="message">{t('billing.explanation')}</Alert>
      </div>
    </Panel>
  );
});

export default dashboardWrapper(BillingPage, t('menu.dashboard.billing'));
